/**
 * WidgetFiltersConfig - Configuration des filtres pour un widget
 * 
 * Composant pour configurer les filtres hiérarchiques d'un widget.
 * Similaire aux filtres de dashboard : période, recherche, trackers.
 */

import React, { useState, useMemo, useCallback } from "react";
import { Typography, Button, Switch, Input } from "@/components/ui";
import useTranslation from "@/hooks/use-translation";
import LuceneTextarea from "@/components/ui/lucene-textarea";
import DateRangePicker from "@/components/monitoring/DateRangePicker";
import TrackerGroupSelector from "@/components/tracker-group-selector";
import {
  FaSearch,
  FaDatabase,
  FaTh,
  FaFolder,
} from "react-icons/fa";
import { useTrackers } from "@/api/tracker";
import { useFacets } from "@/api";
import type { Tracker } from "@/models/tracker";
import TrackersViewWrapper from "@/features/trackers/components/trackers-view-wrapper";
import type { TrackerViewMode } from "@/features/trackers/tracker-view-selector";
import type { WidgetHierarchicalFilters } from "@/models/widget-v2";
import type { MentionField } from "@/hooks/use-mention-autocomplete";

// ============================================================================
// Types
// ============================================================================

interface WidgetFiltersConfigProps {
  /** Si true, les filtres indépendants sont activés */
  independentFilters: boolean;
  /** Callback pour activer/désactiver les filtres indépendants */
  onIndependentFiltersChange: (value: boolean) => void;
  /** Filtres hiérarchiques actuels */
  filters?: WidgetHierarchicalFilters;
  /** Callback pour mettre à jour les filtres */
  onFiltersChange: (filters: WidgetHierarchicalFilters) => void;
}

// ============================================================================
// Constants
// ============================================================================

// Period chips configuration
const PERIOD_CHIPS = [
  { value: "", label: "Tout" },
  { value: "last_7_days", label: "7j" },
  { value: "last_30_days", label: "30j" },
  { value: "last_90_days", label: "90j" },
  { value: "custom", label: "Personnalisé" },
];

// ============================================================================
// Component
// ============================================================================

export const WidgetFiltersConfig: React.FC<WidgetFiltersConfigProps> = ({
  independentFilters,
  onIndependentFiltersChange,
  filters,
  onFiltersChange,
}) => {
  const { t } = useTranslation();
  // API data
  const { data: trackersData, isLoading: trackersLoading } = useTrackers();
  const { data: facets } = useFacets();

  const mentionFields: MentionField[] = useMemo(() => {
    if (!facets || facets.length === 0) return [];
    return facets.map((f) => ({ key: f.key, type: f.type || "text" }));
  }, [facets]);

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectionMode, setSelectionMode] = useState<"grid" | "groups">("groups");
  const [viewMode, setViewMode] = useState<TrackerViewMode>("board");
  const [groupBy, setGroupBy] = useState<"none" | "source" | "tags" | "status" | "type">("source");

  // Update filters helper
  const updateFilters = useCallback(
    (updates: Partial<WidgetHierarchicalFilters>) => {
      onFiltersChange({
        ...filters,
        ...updates,
      });
    },
    [filters, onFiltersChange]
  );

  // Period helpers
  const dateRange = filters?.date_range || "";
  const dateFrom = filters?.date_from || null;
  const dateTo = filters?.date_to || null;
  const filterSearch = filters?.search || "";
  const selectedTrackerIds = filters?.trackers || [];

  // Convert trackers to the right format
  const convertedTrackers: Tracker[] = useMemo(() => {
    return trackersData || [];
  }, [trackersData]);

  // Handle tracker selection change
  const handleTrackerSelectionChange = useCallback(
    (newIds: string[]) => {
      updateFilters({ trackers: newIds.length > 0 ? newIds : null });
    },
    [updateFilters]
  );

  // Handle individual tracker toggle
  const handleTrackerToggle = useCallback(
    (trackerId: string) => {
      const currentIds = selectedTrackerIds || [];
      const newIds = currentIds.includes(trackerId)
        ? currentIds.filter((id) => id !== trackerId)
        : [...currentIds, trackerId];
      handleTrackerSelectionChange(newIds);
    },
    [selectedTrackerIds, handleTrackerSelectionChange]
  );

  // Handle date range change
  const handleDateRangeChange = useCallback(
    (from: string | null, to: string | null) => {
      updateFilters({
        date_from: from,
        date_to: to,
      });
    },
    [updateFilters]
  );

  // Handle select/deselect all
  const handleSelectAll = useCallback(() => {
    if (trackersData) {
      const allIds = trackersData.map((t) => t.id);
      handleTrackerSelectionChange(allIds);
    }
  }, [trackersData, handleTrackerSelectionChange]);

  const handleDeselectAll = useCallback(() => {
    handleTrackerSelectionChange([]);
  }, [handleTrackerSelectionChange]);

  // Selected count
  const selectedCount = selectedTrackerIds?.length || 0;

  return (
    <div className="space-y-6">
      {/* Independent filters toggle */}
      <div className="p-4 rounded-lg bg-arc-surface-secondary border border-arc-border">
        <Switch
          label="Filtres indépendants"
          checked={independentFilters}
          onChange={onIndependentFiltersChange}
        />
        <Typography variant="text-xs" className="text-content-tertiary mt-2">
          {independentFilters
            ? "Le widget utilise uniquement ses propres filtres, ignorant les filtres du dashboard."
            : "Les filtres ci-dessous s'ajoutent aux filtres globaux du dashboard."}
        </Typography>
      </div>

      {/* Filters configuration - always visible */}
      <div className="space-y-5">
          {/* Period chips */}
          <div className="flex flex-col gap-2">
            <Typography variant="text-sm" className="font-medium text-content-secondary">
              Période
            </Typography>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex gap-1.5 flex-wrap">
                {PERIOD_CHIPS.map((chip) => (
                  <button
                    key={chip.value}
                    type="button"
                    onClick={() => updateFilters({ date_range: chip.value || null })}
                    className={`
                      px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-150
                      ${
                        dateRange === chip.value
                          ? "bg-accent text-white shadow-sm"
                          : "bg-surface-secondary text-content-secondary hover:bg-surface-tertiary hover:text-content-primary"
                      }
                    `}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              {dateRange === "custom" && (
                <div className="ml-2">
                  <DateRangePicker
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    onChange={handleDateRangeChange}
                    size="sm"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Search section */}
          <div>
            <Typography variant="text-sm" className="font-medium text-content-primary mb-2 flex items-center gap-2">
              <FaSearch className="text-content-tertiary" />
              Recherche (syntaxe Lucene)
            </Typography>
            <LuceneTextarea
              placeholder="Recherche appliquée au widget... (ex: title:bitcoin AND sentiment:positive)"
              value={filterSearch}
              onChange={(e) => updateFilters({ search: e.target.value || null })}
              leftIcon={<FaSearch className="text-content-tertiary" />}
              minRows={2}
              availableFields={mentionFields}
              maxRows={3}
            />
            <Typography variant="text-xs" className="text-content-tertiary mt-1">
              Supporte la syntaxe Lucene. Tapez{" "}
              <span className="font-mono bg-surface-secondary px-1 rounded">@</span>{" "}
              pour insérer un nom de champ.
            </Typography>
          </div>

          {/* Trackers section */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <Typography variant="text-sm" className="font-semibold text-content-primary flex items-center gap-2">
                <FaDatabase className="text-content-tertiary" />
                Sélection des trackers
              </Typography>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                  Tout sélectionner
                </Button>
                <Button variant="ghost" size="sm" onClick={handleDeselectAll}>
                  Tout désélectionner
                </Button>
              </div>
            </div>

            {/* Selected count */}
            <Typography variant="text-xs" className="text-content-secondary">
              {t("n_trackers_selected", { count: selectedCount })}
            </Typography>

            {/* Selection mode toggle */}
            <div className="flex items-center gap-2">
              <Typography variant="text-xs" className="text-content-secondary">Vue :</Typography>
              <div className="flex rounded-md border border-arc-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setSelectionMode("groups")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                    selectionMode === "groups"
                      ? "bg-accent text-white"
                      : "bg-surface-secondary text-content-secondary hover:bg-surface-tertiary hover:text-content-primary"
                  }`}
                >
                  <FaFolder size={12} />
                  Par groupes
                </button>
                <button
                  type="button"
                  onClick={() => setSelectionMode("grid")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                    selectionMode === "grid"
                      ? "bg-accent text-white"
                      : "bg-surface-secondary text-content-secondary hover:bg-surface-tertiary hover:text-content-primary"
                  }`}
                >
                  <FaTh size={12} />
                  Grille
                </button>
              </div>
            </div>

            {/* Trackers loading state */}
            {trackersLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="text-accent mb-2">
                    <FaDatabase className="text-3xl mx-auto animate-pulse" />
                  </div>
                  <Typography variant="text-sm" className="text-content-secondary">
                    Chargement des trackers...
                  </Typography>
                </div>
              </div>
            ) : (
              <>
                {/* Group selection mode */}
                {selectionMode === "groups" && (
                  <TrackerGroupSelector
                    trackers={convertedTrackers}
                    selectedIds={selectedTrackerIds || []}
                    onSelectionChange={handleTrackerSelectionChange}
                    maxHeight="200px"
                  />
                )}

                {/* Grid selection mode */}
                {selectionMode === "grid" && (
                  <>
                    {/* Search */}
                    <Input
                      placeholder="Filtrer les trackers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      leftIcon={<FaSearch className="text-content-tertiary" />}
                      size="sm"
                    />

                    {/* Trackers view */}
                    <div className="max-h-[200px] overflow-y-auto border border-arc-border rounded-arc-md">
                      <TrackersViewWrapper
                        trackers={convertedTrackers}
                        selectedIds={selectedTrackerIds || []}
                        onSelect={(trackerId) => handleTrackerToggle(trackerId)}
                        searchTerm={searchTerm}
                        selectedTags={[]}
                        groupBy={groupBy}
                        viewMode={viewMode}
                        onViewChange={setViewMode}
                        showViewSelector={false}
                        showGroupSelector={false}
                        className="min-h-0"
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
    </div>
  );
};

export default WidgetFiltersConfig;
